import React, { useRef, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon CCMS — Eylül 2026 Sosyal Medya İçerik & Medya Planı.
 * Ağustos CCMS planıyla (CanonCCMSContentPlanAugust) aynı çatı, aynı içerik mix'i ve
 * aynı bütçe modeli (Efe kararı 2026-09-09: "Ağustos'un aynısı, 45K brüt").
 * 2 frame (Future of What's Possible / Future of People), 4 pillar (içerik),
 * FB + IG post bazlı dağılım. Her post 2.500₺. Net medya 32.500₺.
 * Bütçe modeli: brüt 45.000₺ → stopaj + hizmet bedeli kesintisi → 32.500₺ net.
 *
 * Ağustos'tan FARKI — tahminler artık VARSAYIMSAL DEĞİL. Temmuz ve Ağustos
 * planlarında CPM 28 / CPC 2,80 / CPE 0,55 elle yazılmıştı; bu sayfada aynı
 * yerler Canon'un kendi Meta hesabının (act_3256944827800966) gerçekleşmesinden
 * geliyor:
 *   CPM  ₺24,32 — Ağustos CCMS akışı, yalnız FB + IG yerleşimleri
 *                 (₺49.783 harcama / 2.047.306 gösterim)
 *   CPC  ₺2,41  — Haz–Eyl 2026 TRAFİK hedefli kampanyalar, FB + IG
 *                 (₺332.391 harcama / 137.797 link tıklaması)
 *   CPE  ₺1,29  — Ağustos CCMS, VİDEO HARİÇ etkileşim
 *                 (₺56.647 harcama / 43.907 etkileşim)
 *   Frekans 2,04 — Ağustos 2.199.879 gösterim / 1.076.435 tekil erişim
 * Trafik CPC'si Ağustos'un kendi trafik satırlarından ALINMADI: Ağustos'ta
 * Tuesday Tip ve Technology Highlight planda Traffic yazmasına rağmen Meta'da
 * Engagement hedefiyle açılmış, o yüzden link tıklaması ölçülemez durumda
 * (₺9.963 harcamaya 121 tıklama). Bu, Eylül'de düzeltilmesi gereken tek
 * kurulum hatası — sayfada uyarı olarak duruyor.
 */
export default function CanonCCMSContentPlanSeptember() {
  const slideRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const fmtCur = (n) => '₺' + Math.round(Number(n)).toLocaleString('tr-TR');
  const fmtInt = (n) => Math.round(Number(n)).toLocaleString('tr-TR');

  // ── Bütçe modeli ───────────────────────────────────────────────────────────
  const PER_POST = 2500;             // post başına medya bütçesi
  const GROSS = 45000;               // müşteri brüt bütçesi
  const NET = 32500;                 // stopaj + hizmet bedeli sonrası net medya
  const DEDUCT = GROSS - NET;        // 12.500 — stopaj + hizmet bedeli vb.
  const LIVE_DAYS = 5;               // CCMS kampanyaları 5 gün yayında

  // ── İçerik / pillar dağılımı (Ağustos ile birebir aynı) ────────────────────
  // sprout: müşterinin CEA matrisindeki "Sprout Label Name CEA" karşılığı.
  const pillarDefs = [
    { frame: "Future of What's Possible", name: 'Tuesday Tip – Haftanın Canon İpucu', fb: 1, ig: 1, objective: 'Traffic', sprout: 'CORP_Tuesday Tip', highlight: true },
    { frame: "Future of What's Possible", name: 'Technology Highlight', fb: 1, ig: 1, objective: 'Traffic', sprout: 'CORP_Technology Highlight' },
    { frame: 'Future of People', name: 'UGC – Theme Announcement', fb: 1, ig: 1, objective: 'Engagement', sprout: 'CORP_UGC', highlight: true },
    { frame: 'Future of People', name: 'UGC – Reshare', fb: 2, ig: 5, objective: 'Engagement', sprout: 'CORP_UGC' },
  ];
  const pillars = pillarDefs.map((p) => {
    const posts = p.fb + p.ig;
    return { ...p, posts, budget: posts * PER_POST };
  });

  const totals = pillars.reduce((t, p) => ({
    fb: t.fb + p.fb, ig: t.ig + p.ig, posts: t.posts + p.posts, budget: t.budget + p.budget,
  }), { fb: 0, ig: 0, posts: 0, budget: 0 });

  const byObjective = pillars.reduce((m, p) => {
    m[p.objective] = (m[p.objective] || 0) + p.budget;
    return m;
  }, {});
  const trafficBudget = byObjective.Traffic || 0;       // 10.000
  const engagementBudget = byObjective.Engagement || 0; // 22.500

  // ── Tahmini performans — Canon'un GERÇEKLEŞEN rakamlarından ───────────────
  // Kaynak ve örneklem büyüklükleri dosyanın başındaki açıklamada.
  const CPM = 24.32;  // Ağustos CCMS, FB + IG yerleşimleri
  const FREQ = 2.04;  // Ağustos gösterim ÷ tekil erişim
  const CPC = 2.41;   // Haz–Eyl trafik hedefli kampanyalar, FB + IG, link tıklaması
  const CPE = 1.29;   // Ağustos CCMS, video hariç etkileşim
  const impressions = Math.round((NET / CPM) * 1000);
  const reach = Math.round(impressions / FREQ);
  const clicks = Math.round(trafficBudget / CPC);
  const engagements = Math.round(engagementBudget / CPE);


  const exportPdf = useCallback(async () => {
    if (exporting || !slideRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(slideRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_CCMS_Icerik_Plani_Eylul_2026.pdf');
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  const Stat = ({ label, value, sub, tone = 'gray' }) => {
    const tones = {
      gray: 'border-gray-200 bg-gray-50 text-gray-800',
      red: 'border-red-100 bg-red-50 text-red-700',
      indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
      green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    };
    return (
      <div className={`rounded-xl border p-4 ${tones[tone]}`}>
        <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
        <p className="text-xl font-bold mt-1">{value}</p>
        {sub && <p className="text-[10px] opacity-60 mt-0.5">{sub}</p>}
      </div>
    );
  };

  const ObjBadge = ({ objective }) => (
    <span className={`inline-block px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
      objective === 'Traffic' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-violet-50 text-violet-700 border-violet-200'
    }`}>{objective}</span>
  );

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="flex justify-end gap-2 mb-3">
        <button
          onClick={exportPdf}
          disabled={exporting}
          className="text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-60"
          style={{ background: '#CC0000' }}
        >
          {exporting ? 'PDF Oluşturuluyor…' : 'PDF Olarak İndir'}
        </button>
      </div>

      <div ref={slideRef} className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-12 w-auto object-contain" />
            <div className="h-8 w-px bg-gray-200" />
            <div>
              <p className="text-xs font-bold text-gray-800">CCMS — Sosyal Medya İçerik &amp; Medya Planı · Eylül 2026</p>
              <p className="text-[9px] text-gray-400">Meta Ads (FB + IG) — Traffic &amp; Engagement · CCMS kampanyaları 5 gün yayında</p>
            </div>
          </div>
          <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-12 w-auto object-contain" />
        </div>

        <div className="p-6 space-y-6">
          {/* Plan özeti */}
          <div className="rounded-xl border border-red-100 bg-red-50 p-4">
            <p className="text-sm font-bold text-gray-800">Plan Özeti — Eylül 2026</p>
            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
              Canon CCMS sosyal medya içerik planı: <strong>2 frame</strong> (Future of What's Possible / Future of People)
              altında <strong>4 içerik (pillar)</strong>, Facebook + Instagram'da toplam <strong>{totals.posts} gönderi</strong>.
              Her gönderiye <strong>{fmtCur(PER_POST)}</strong> medya bütçesi ayrılır; CCMS kampanyaları <strong>{LIVE_DAYS} gün</strong> yayında kalır.
              Toplam net medya harcaması <strong>{fmtCur(NET)}</strong>. İçerik mix'i ve bütçe modeli <strong>Ağustos 2026 planıyla birebir aynıdır</strong>;
              değişen tek şey tahmin tabanı — bu ay CPM, CPC ve etkileşim maliyeti Canon'un kendi gerçekleşen rakamlarından alındı.
            </p>
          </div>

          {/* Bütçe kırılımı */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Bütçe Kırılımı</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Toplam Bütçe (Brüt)" value={fmtCur(GROSS)} sub="müşteri toplam" />
              <Stat label="Stopaj + Hizmet Bedeli" value={'-' + fmtCur(DEDUCT)} sub="kesintiler" tone="indigo" />
              <Stat label="NET Medya" value={fmtCur(NET)} sub="Meta'da harcanan" tone="green" />
              <Stat label="Post Başına" value={fmtCur(PER_POST)} sub={`${totals.posts} gönderi`} />
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Brüt {fmtCur(GROSS)} bütçeden stopaj + hizmet bedeli ({fmtCur(DEDUCT)}) düşüldükten sonra
              reklamda gerçek harcanan net tutar {fmtCur(NET)}'dir. Hedef bazlı: Traffic {fmtCur(trafficBudget)} · Engagement {fmtCur(engagementBudget)}.
            </p>
          </div>

          {/* İçerik / pillar tablosu */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">İçerik &amp; Bütçe Dağılımı ({pillars.length} Pillar)</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">Frame</th>
                    <th className="text-left px-3 py-2 font-semibold">Pillar (İçerik)</th>
                    <th className="text-left px-3 py-2 font-semibold">Sprout Label</th>
                    <th className="text-right px-3 py-2 font-semibold">FB Post</th>
                    <th className="text-right px-3 py-2 font-semibold">IG Post</th>
                    <th className="text-center px-3 py-2 font-semibold">Hedef</th>
                    <th className="text-right px-3 py-2 font-semibold">Bütçe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pillars.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{p.frame}</td>
                      <td className={`px-3 py-2 font-semibold ${p.highlight ? 'text-red-700' : 'text-gray-800'}`}>{p.name}</td>
                      <td className="px-3 py-2 text-gray-500 font-mono text-[10px]">{p.sprout}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.fb} × {fmtInt(PER_POST)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.ig} × {fmtInt(PER_POST)}</td>
                      <td className="px-3 py-2 text-center"><ObjBadge objective={p.objective} /></td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtCur(p.budget)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2" colSpan={3}>TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals.fb} post</td>
                    <td className="px-3 py-2 text-right tabular-nums">{totals.ig} post</td>
                    <td className="px-3 py-2 text-center">{totals.posts} gönderi</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtCur(totals.budget)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Tahmini performans */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Tahmini Performans</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Toplam Gösterim" value={fmtInt(impressions)} sub={`CPM ≈ ₺${CPM}`} />
              <Stat label="Tahmini Erişim" value={fmtInt(reach)} sub={`~${FREQ} frekans`} tone="green" />
              <Stat label="Trafik Tıklaması" value={fmtInt(clicks)} sub={`${fmtCur(trafficBudget)} · CPC ≈ ₺${CPC.toFixed(2)}`} tone="red" />
              <Stat label="Etkileşim" value={fmtInt(engagements)} sub={`${fmtCur(engagementBudget)} · CPE ≈ ₺${CPE.toFixed(2)}`} tone="indigo" />
            </div>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed">
              Tahminler <strong>varsayım değil</strong>: CPM ₺{CPM.toFixed(2)} Ağustos CCMS akışının FB + IG gerçekleşmesi
              (₺49.783 / 2.047.306 gösterim), frekans {FREQ} Ağustos'un gösterim ÷ tekil erişim oranı,
              CPC ₺{CPC.toFixed(2)} Haziran–Eylül trafik hedefli kampanyaların FB + IG link tıklama maliyeti
              (₺332.391 / 137.797 tık), CPE ₺{CPE.toFixed(2)} Ağustos'un <strong>video hariç</strong> etkileşim maliyeti.
              Sayılar Meta açık artırma dinamiklerine göre değişir; optimizasyonla yön verilir.
            </p>

            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-[11px] font-bold text-amber-900">Ağustos'tan taşınan tek düzeltme</p>
              <p className="text-[11px] text-amber-900 mt-1 leading-relaxed">
                Ağustos'ta <strong>Tuesday Tip</strong> ve <strong>Technology Highlight</strong> planda Traffic hedefiyle
                yazılmasına rağmen Meta'da <strong>Engagement</strong> hedefiyle açılmış. ₺9.963 harcamaya karşılık
                yalnızca 121 link tıklaması geldi, çünkü Meta bu kampanyalarda tıklamaya değil etkileşime optimize etti.
                Eylül'de bu iki pillar <strong>Traffic</strong> hedefiyle kurulacak; yukarıdaki
                {' '}{fmtInt(clicks)} tıklama tahmini buna dayanıyor.
              </p>
              <p className="text-[11px] text-amber-900 mt-2 leading-relaxed">
                İkinci düzeltme yerleşimde: Ağustos'ta otomatik yerleşim bütçenin %12,1'ini
                (₺6.864) Audience Network ve Threads'e dağıttı. Audience Network tek başına ₺6.532 harcayıp
                <strong> %0,01 etkileşim oranı</strong> üretti ve bu gösterimler KPI hesabına girmiyor.
                Eylül'de adset seviyesinde yerleşim FB + IG ile sınırlanacak.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <img src="/Canon_logo_vector.png" alt="Canon" className="h-7 w-auto object-contain" />
            <p className="text-[9px] text-gray-400">
              Eylül 2026 · Confidential · Jellybon × Glowytics
            </p>
            <img src="/Jellybon-digital-agency-istanbul.webp" alt="Jellybon" className="h-6 w-auto object-contain" />
          </div>
        </div>
      </div>
    </div>
  );
}
